import { DateRange } from "../../../dashboard-framework/types";
import { CUSTOMER_SUPPORT_ROLE_IDS, NfbFunctionKey } from "./neurofeedbackProductivityConfig";

// Rollup 100% client-side a partir de fetches raw de três coleções (schedules + sessions +
// evaluations). Chave de rollup = directus_users.id (a única partilhada): schedules.user é o
// utilizador; sessions/evaluations chegam ao utilizador via technician.user_id /
// brain_maps_technician.user_id. As decisões load-bearing (validadas em spec.md) vivem aqui:
// dias em fuso de Lisboa, exclusão de sessões internas no efetivo (feita no fetch), crédito de
// reunião aos participantes (internal_meetings_participants), e clamp do tempo morto a 0.
//
// Ajuste 17/07/2026 (Jorge): considera-se QUALQUER serviço (não só NFB/EEG) tanto no horário como
// nas sessões, e o universo de linhas passa a ser TODOS os colaboradores com horário e/ou sessões
// feitas no período. A análise por função faz-se com o filtro "Função" (aplicado no componente).

export type RawTechnician = {
    id: number;
    user_id: string | null;
    specializations?: Array<string> | null;
};

export type RawSchedule = {
    id: number;
    start_date: string;
    end_date: string;
    user: string | null;
};

type SessionTechnicianRef = { user_id: string | null } | null;
type InternalParticipantRef = { technicians_id: { user_id: string | null } | null } | null;

export type RawDoneSession = {
    id: number;
    start_date: string;
    end_date: string;
    technician?: SessionTechnicianRef;
};

export type RawInternalSession = {
    id: number;
    start_date: string;
    end_date: string;
    internal_meetings_participants?: Array<InternalParticipantRef> | null;
};

export type RawEvaluation = {
    id: number;
    brain_maps_date: string | null;
    brain_maps_technician?: { user_id: string | null } | null;
};

export type PersonInfo = {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    role?: string | { id?: string } | null;
};

export type NeurofeedbackInputs = {
    schedules: Array<RawSchedule>;
    doneSessions: Array<RawDoneSession>;
    internalSessions: Array<RawInternalSession>;
    evaluations: Array<RawEvaluation>;
    peopleById: Record<string, PersonInfo>;
    specializationsByUserId: Record<string, Array<string>>;
};

export const UNKNOWN_PERSON_NAME = "Utilizador desconhecido";

const MS_PER_HOUR = 1000 * 60 * 60;

// Dia em fuso de Lisboa (yyyy-MM-dd; en-CA formata em ISO). O recorte do período usa SEMPRE
// este dia, não o dia UTC — uma sessão perto da meia-noite cai no dia certo mesmo com DST.
const lisbonDayFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Lisbon",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
});

export const lisbonDayKey = (isoTimestamp: string | null | undefined) => {
    if (!isoTimestamp) return null;
    const dayKey = lisbonDayFormatter.format(new Date(isoTimestamp));
    return dayKey;
};

export const durationHours = (startIso: string, endIso: string) => {
    const milliseconds = new Date(endIso).getTime() - new Date(startIso).getTime();
    const hours = milliseconds > 0 ? milliseconds / MS_PER_HOUR : 0;
    return hours;
};

const isStartInPeriod = (isoTimestamp: string | null | undefined, range: DateRange) => {
    const dayKey = lisbonDayKey(isoTimestamp);
    const inPeriod = dayKey !== null && dayKey >= range.gte && dayKey < range.lt;
    return inPeriod;
};

// Especializações por utilizador, unidas por TODOS os registos de técnico do mesmo user_id
// (um utilizador pode ter várias fichas de técnico — não usar o atalho technician_id[0]).
export const specializationsByUserIdOf = (technicians: Array<RawTechnician>) => {
    const byUserId: Record<string, Array<string>> = {};
    technicians.forEach((technician) => {
        const userId = technician.user_id || null;
        if (!userId) return;
        const existing = byUserId[userId] || [];
        const merged = Array.from(new Set([...existing, ...(technician.specializations || [])]));
        byUserId[userId] = merged;
    });
    return byUserId;
};

// Função ÚNICA por técnico, por prioridade: Neurofeedback > Psicologia > Apoio ao cliente > Outros.
// Baseada na mesma classificação da team-tasks (especializações + role), mas escolhe UMA função para
// o técnico entrar numa só tabela: quem tem neurofeedback E psicologia (ex.: Sara, Sandra) fica só em
// Neurofeedback. As MÉTRICAS da linha continuam a contar toda a atividade da pessoa (qualquer
// serviço) — a função é só o grupo/tabela, não recorta as sessões nem o horário.
export const primaryFunctionOf = (specializations: Array<string>, roleId: string | null): NfbFunctionKey => {
    if (specializations.includes("neurofeedback")) return "neurofeedback";
    if (specializations.includes("psicologia")) return "psicologia";
    if (roleId && CUSTOMER_SUPPORT_ROLE_IDS.includes(roleId)) return "apoio_cliente";
    return "outros";
};

const roleIdOf = (person: PersonInfo | undefined) => {
    if (!person || !person.role) return null;
    const roleId = typeof person.role === "string" ? person.role : person.role.id || null;
    return roleId;
};

// Todos os user_ids que aparecem nos dados (superset — inclui blocos de duração 0). Serve para o
// lookup de pessoas; a inclusão da LINHA é decidida por atividade > 0 em neurofeedbackProductivityRows.
export const collectActiveUserIds = (inputs: Pick<NeurofeedbackInputs, "schedules" | "doneSessions" | "internalSessions" | "evaluations">) => {
    const userIds = new Set<string>();
    inputs.schedules.forEach((schedule) => {
        if (schedule.user) userIds.add(schedule.user);
    });
    inputs.doneSessions.forEach((session) => {
        const userId = session.technician?.user_id || null;
        if (userId) userIds.add(userId);
    });
    inputs.internalSessions.forEach((session) => {
        (session.internal_meetings_participants || []).forEach((participant) => {
            const userId = participant?.technicians_id?.user_id || null;
            if (userId) userIds.add(userId);
        });
    });
    inputs.evaluations.forEach((evaluation) => {
        const userId = evaluation.brain_maps_technician?.user_id || null;
        if (userId) userIds.add(userId);
    });
    return Array.from(userIds);
};

export type NeurofeedbackRow = {
    userId: string;
    displayName: string;
    functionKey: NfbFunctionKey;
    horarioHours: number;
    efetivoHours: number;
    reunioesHours: number;
    mortoHours: number;
    mortoPercent: number | null;
    workedDays: number;
    mortoPerWorkedDayHours: number | null;
    brainMaps: number;
    ratio: number | null;
};

const displayNameOf = (person: PersonInfo | undefined) => {
    if (!person) return UNKNOWN_PERSON_NAME;
    const composedName = [person.first_name, person.last_name].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    const displayName = composedName || person.email || UNKNOWN_PERSON_NAME;
    return displayName;
};

// Crédito da reunião: todos os participantes (internal_meetings_participants), sem duplicar o
// mesmo utilizador na mesma sessão. (As reuniões não têm "dono" — technician é nulo.)
const attendeesOfInternalSession = (session: RawInternalSession) => {
    const participantUserIds = (session.internal_meetings_participants || [])
        .map((participant) => participant?.technicians_id?.user_id || null)
        .filter((userId): userId is string => Boolean(userId));
    const attendees = Array.from(new Set(participantUserIds));
    return attendees;
};

const addHours = (totalsByUser: Map<string, number>, userId: string, amount: number) => {
    totalsByUser.set(userId, (totalsByUser.get(userId) || 0) + amount);
};

const addWorkedDay = (workedDaysByUser: Map<string, Set<string>>, userId: string, dayKey: string) => {
    const workedDays = workedDaysByUser.get(userId) || new Set<string>();
    workedDays.add(dayKey);
    workedDaysByUser.set(userId, workedDays);
};

const perWorkedDay = (mortoHours: number, workedDays: number) => {
    const average = workedDays > 0 ? mortoHours / workedDays : null;
    return average;
};

export const neurofeedbackProductivityRows = (inputs: NeurofeedbackInputs, range: DateRange) => {
    const horarioByUser = new Map<string, number>();
    const efetivoByUser = new Map<string, number>();
    const reunioesByUser = new Map<string, number>();
    const brainMapsByUser = new Map<string, number>();
    const workedDaysByUser = new Map<string, Set<string>>();

    inputs.schedules.forEach((schedule) => {
        const userId = schedule.user || null;
        if (!userId) return;
        if (!isStartInPeriod(schedule.start_date, range)) return;
        const hours = durationHours(schedule.start_date, schedule.end_date);
        addHours(horarioByUser, userId, hours);
        if (hours > 0) addWorkedDay(workedDaysByUser, userId, lisbonDayKey(schedule.start_date) as string);
    });

    inputs.doneSessions.forEach((session) => {
        const userId = session.technician?.user_id || null;
        if (!userId) return;
        if (!isStartInPeriod(session.start_date, range)) return;
        addHours(efetivoByUser, userId, durationHours(session.start_date, session.end_date));
    });

    inputs.internalSessions.forEach((session) => {
        if (!isStartInPeriod(session.start_date, range)) return;
        const hours = durationHours(session.start_date, session.end_date);
        attendeesOfInternalSession(session).forEach((userId) => addHours(reunioesByUser, userId, hours));
    });

    inputs.evaluations.forEach((evaluation) => {
        const userId = evaluation.brain_maps_technician?.user_id || null;
        if (!userId) return;
        if (!isStartInPeriod(evaluation.brain_maps_date, range)) return;
        brainMapsByUser.set(userId, (brainMapsByUser.get(userId) || 0) + 1);
    });

    const activeUserIds = new Set<string>();
    const collectActive = (totalsByUser: Map<string, number>) =>
        totalsByUser.forEach((value, userId) => {
            if (value > 0) activeUserIds.add(userId);
        });
    [horarioByUser, efetivoByUser, reunioesByUser].forEach(collectActive);
    brainMapsByUser.forEach((count, userId) => {
        if (count > 0) activeUserIds.add(userId);
    });

    // Só colaboradores com registo na coleção de técnicos (as chaves do mapa de especializações são
    // exatamente os user_ids com ficha de técnico). Receção/backoffice sem ficha ficam de fora.
    const technicianUserIdSet = new Set(Object.keys(inputs.specializationsByUserId));

    const rows: Array<NeurofeedbackRow> = [];
    activeUserIds.forEach((userId) => {
        if (!technicianUserIdSet.has(userId)) return;
        const horarioHours = horarioByUser.get(userId) || 0;
        const efetivoHours = efetivoByUser.get(userId) || 0;
        const reunioesHours = reunioesByUser.get(userId) || 0;
        const mortoHours = Math.max(0, horarioHours - efetivoHours - reunioesHours);
        const mortoPercent = horarioHours > 0 ? (mortoHours / horarioHours) * 100 : null;
        const workedDays = workedDaysByUser.get(userId)?.size || 0;
        const brainMaps = brainMapsByUser.get(userId) || 0;
        const ratio = mortoHours > 0 ? brainMaps / mortoHours : null;
        const specializations = inputs.specializationsByUserId[userId] || [];
        const functionKey = primaryFunctionOf(specializations, roleIdOf(inputs.peopleById[userId]));
        const row = {
            userId,
            displayName: displayNameOf(inputs.peopleById[userId]),
            functionKey,
            horarioHours,
            efetivoHours,
            reunioesHours,
            mortoHours,
            mortoPercent,
            workedDays,
            mortoPerWorkedDayHours: perWorkedDay(mortoHours, workedDays),
            brainMaps,
            ratio,
        };
        rows.push(row);
    });

    // Métrica em foco: % de tempo morto, decrescente (quem tem mais tempo morto fica no topo, para
    // saltar à vista); linhas sem horário lançado (sem %) por último; nome a desempatar.
    rows.sort((firstRow, secondRow) => {
        const firstPercent = firstRow.mortoPercent === null ? -1 : firstRow.mortoPercent;
        const secondPercent = secondRow.mortoPercent === null ? -1 : secondRow.mortoPercent;
        if (secondPercent !== firstPercent) return secondPercent - firstPercent;
        const nameComparison = firstRow.displayName.localeCompare(secondRow.displayName, "pt");
        return nameComparison;
    });
    return rows;
};

export type NeurofeedbackTotals = {
    horarioHours: number;
    efetivoHours: number;
    reunioesHours: number;
    mortoHours: number;
    mortoPercent: number | null;
    workedDays: number;
    mortoPerWorkedDayHours: number | null;
    brainMaps: number;
    ratio: number | null;
};

export const neurofeedbackTotals = (rows: Array<NeurofeedbackRow>): NeurofeedbackTotals => {
    const summed = rows.reduce(
        (accumulator, row) => ({
            horarioHours: accumulator.horarioHours + row.horarioHours,
            efetivoHours: accumulator.efetivoHours + row.efetivoHours,
            reunioesHours: accumulator.reunioesHours + row.reunioesHours,
            mortoHours: accumulator.mortoHours + row.mortoHours,
            workedDays: accumulator.workedDays + row.workedDays,
            brainMaps: accumulator.brainMaps + row.brainMaps,
        }),
        { horarioHours: 0, efetivoHours: 0, reunioesHours: 0, mortoHours: 0, workedDays: 0, brainMaps: 0 }
    );
    const ratio = summed.mortoHours > 0 ? summed.brainMaps / summed.mortoHours : null;
    const mortoPercent = summed.horarioHours > 0 ? (summed.mortoHours / summed.horarioHours) * 100 : null;
    const mortoPerWorkedDayHours = perWorkedDay(summed.mortoHours, summed.workedDays);
    const totals = { ...summed, mortoPercent, mortoPerWorkedDayHours, ratio };
    return totals;
};
